import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useUser } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { canAccessUserManagement } from '../../utils/permissionUtils';
import './AppHeader.css';

// Selector de idioma (ESP/ENG). Referente: investigacion.uc.cl (globo + ESP / ENG,
// separador "/"). `onDark` lo adapta al header azul con escala de blancos; sin
// `onDark` (drawer mobile, fondo blanco) usa los tonos azules del referente.
const LanguageToggle: React.FC<{ small?: boolean; onDark?: boolean }> = ({ small, onDark }) => {
  const { language, setLanguage } = useLanguage();
  const activeColor = onDark ? 'var(--color-text-on-color)' : 'var(--blue-80)';
  const idleColor = onDark ? 'var(--color-white-transparent)' : 'var(--color-text-subtle)';
  const globeColor = onDark ? 'var(--color-text-on-color)' : 'var(--color-primary)';
  const slashColor = onDark ? 'var(--color-border-on-color)' : 'var(--color-primary)';
  const base: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.15rem 0.1rem',
    fontFamily: 'inherit',
    fontSize: small ? '0.8rem' : '0.875rem',
    lineHeight: 1,
    letterSpacing: '0.02em',
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.1rem' }}>
      {/* Globo del kit + ESP / ENG, como el referente de investigacion.uc.cl */}
      <i
        className="uc-icon"
        aria-hidden="true"
        style={{
          fontSize: small ? '0.95rem' : '1.05rem',
          color: globeColor,
          marginRight: '0.05rem',
        }}
      >
        language
      </i>
      <button
        onClick={() => setLanguage('es')}
        style={{
          ...base,
          fontWeight: language === 'es' ? 700 : 400,
          color: language === 'es' ? activeColor : idleColor,
        }}
        aria-pressed={language === 'es'}
      >
        ESP
      </button>
      <span aria-hidden="true" style={{ color: slashColor, fontSize: small ? '0.9rem' : '1rem' }}>
        /
      </span>
      <button
        onClick={() => setLanguage('en')}
        style={{
          ...base,
          fontWeight: language === 'en' ? 700 : 400,
          color: language === 'en' ? activeColor : idleColor,
        }}
        aria-pressed={language === 'en'}
      >
        ENG
      </button>
    </span>
  );
};

// Header global de la plataforma (variante header--systems del kit UC).
// App-específico: conoce el nombre del sistema, los roles y las rutas de admin.
// Depende de AuthContext (useAuth/useUser), LanguageContext (useLanguage) y el
// router. Estilos globales en AppHeader.css (overrides a clases del kit).
const AppHeader: React.FC = () => {
  const { logout } = useAuth();
  const user = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      // Clear any stored navigation state and redirect to login
      navigate('/login', { replace: true, state: {} });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, clear navigation state
      navigate('/login', { replace: true, state: {} });
    }
  };

  // Menú del avatar (cuenta / cerrar sesión) y drawer mobile.
  const [avatarOpen, setAvatarOpen] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const avatarRef = React.useRef<HTMLDivElement>(null);
  const avatarHintDoneRef = React.useRef(false);

  // Cerrar el dropdown del avatar al click fuera o con Escape.
  React.useEffect(() => {
    if (!avatarOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAvatarOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [avatarOpen]);

  // Al entrar a un dashboard tras iniciar sesión, abrir el dropdown del avatar
  // un instante como pista de que es desplegable, y cerrarlo solo tras ~1.5s
  // (una sola vez por carga de la app).
  React.useEffect(() => {
    if (!user || avatarHintDoneRef.current) return;
    if (!/^\/(researcher|evaluator|admin)\/dashboard/.test(location.pathname)) return;
    avatarHintDoneRef.current = true;
    setAvatarOpen(true);
    const t = window.setTimeout(() => setAvatarOpen(false), 1500);
    return () => window.clearTimeout(t);
  }, [user, location.pathname]);

  // Al cerrar sesión (user → null) cerrar el dropdown y rehabilitar la pista de
  // afordancia para el próximo login. El AppHeader no se desmonta entre logout y
  // login, así que sin esto avatarOpen quedaba en true de forma persistente.
  React.useEffect(() => {
    if (!user) {
      setAvatarOpen(false);
      avatarHintDoneRef.current = false;
    }
  }, [user]);

  // Cerrar el drawer mobile al navegar.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Iniciales (máx. 2) para el avatar, con fallback.
  const initials =
    (user?.name || '')
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join('') || 'US';

  const roleLabel =
    user?.role === 'admin'
      ? 'Administrador'
      : user?.role === 'evaluator'
        ? 'Evaluador'
        : 'Investigador';

  const isAdmin = user?.role === 'admin';

  return (
    <header className="uc-header">
      <nav className="uc-navbar navbar-dark uc-navbar-system">
        {/* Desktop (≥xl): header--systems azul. Logo (invertido a blanco),
            nombre del sistema, nav por rol, idioma y avatar con menú de cuenta. */}
        {/* Barra full-bleed, pero el contenido va en una columna centrada de
            1200px (igual que el .headerContainer/.header del PageHeader y el
            .container del contenido) para que "UC" quede alineado con el título
            de página y con la columna de contenido, sin importar el ancho. */}
        <div className="d-none d-xl-block" style={{ padding: 'var(--spacing-12) var(--spacing-32)' }}>
        <div
          className="d-flex align-items-center"
          style={{ gap: 'var(--spacing-80)', maxWidth: '1200px', margin: '0 auto', width: '100%' }}
        >
          {/* Marca "UC" (no el escudo), como el header--systems del Figma. */}
          <span
            className="uc-h2 color-white"
            aria-label="Pontificia Universidad Católica de Chile"
            style={{ margin: 0, flexShrink: 0, fontWeight: 'var(--font-weight-bold)' }}
          >
            UC
          </span>
          {/* Título del sitio "Plataforma de postulación Avanza UC" por el
              Checklist de Contenido de la Dirección de Experiencia Digital
              (rev. 2026-07-20). Debe coincidir con el footer y el <title>.
              Spec Figma: Roboto Slab 24px / peso 400 / LH 28px / LS 0.3px. */}
          <div
            className="text-font--serif color-white"
            style={{
              margin: 0,
              flexShrink: 0,
              fontSize: 'var(--font-size-2xl)',
              fontWeight: 'var(--font-weight-regular)',
              lineHeight: 'var(--font-line-height-lg)',
              letterSpacing: '0.3px',
            }}
          >
            Plataforma de postulación Avanza UC
          </div>
          <div
            className="d-flex align-items-center"
            style={{ gap: '1.5rem', marginLeft: 'auto' }}
          >
            {user && isAdmin && (
              <ul className="uc-navbar_nav">
                {/* Toggle de ROL: se muestra el panel del rol CONTRARIO al ÁREA
                    actual (en /admin → Investigador; en /researcher → Admin).
                    Interino hasta el selector "ver como rol" del PageHeader. */}
                {location.pathname.startsWith('/researcher') && (
                  <li className="nav-item">
                    <Link to="/admin/dashboard" className="uc-btn btn-inline">
                      Panel de Administración
                    </Link>
                  </li>
                )}
                {location.pathname.startsWith('/admin') && (
                  <li className="nav-item">
                    <Link to="/researcher/dashboard" className="uc-btn btn-inline">
                      Panel Investigador
                    </Link>
                  </li>
                )}
                {canAccessUserManagement(user) && (
                  <li className="nav-item">
                    <Link to="/admin/users" className="uc-btn btn-inline">
                      Usuarios
                    </Link>
                  </li>
                )}
              </ul>
            )}
            <LanguageToggle small onDark />
            {user && (
              <div ref={avatarRef} style={{ position: 'relative' }}>
                <button
                  type="button"
                  className="avatar avatar--circle avatar--s avatar--grey"
                  onClick={() => setAvatarOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={avatarOpen}
                  aria-label={`Cuenta de ${user.name}`}
                  style={{ border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <span className="avatar--initials">{initials}</span>
                </button>
                {/* Variante "Avatar + Drop Down" del Figma: indicador de menú
                    desplegable en la esquina inferior-derecha. Decorativo (el
                    click lo maneja el botón del avatar); va fuera del botón
                    porque el .avatar del kit recorta overflow. */}
                <span className="headerAvatarCaret" aria-hidden="true">
                  <i className="uc-icon">arrow_drop_down</i>
                </span>
                {avatarOpen && (
                  <div className="headerAvatarMenu" role="menu">
                    <div className="headerAvatarMenu__head">
                      <div className="headerAvatarMenu__name">{user.name}</div>
                      <div className="headerAvatarMenu__role">{roleLabel}</div>
                    </div>
                    <button
                      type="button"
                      className="headerAvatarMenu__item"
                      role="menuitem"
                      onClick={handleLogout}
                    >
                      <i className="uc-icon" aria-hidden="true">
                        logout
                      </i>
                      Cerrar Sesión
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        </div>
        {/* Mobile (<xl): header-system del Figma en dos filas. Fila 1 (azul):
            hamburguesa + UC + ESP/ENG + avatar. Fila 2 (navy): nombre del sistema.
            La hamburguesa abre un menú con la nav (Admin) + Cerrar Sesión. */}
        <div className="headerMobile d-block d-xl-none">
          <div className="headerMobile__bar">
            {user && (
              <button
                type="button"
                className="headerMobile__burger"
                onClick={() => setMobileOpen((o) => !o)}
                aria-expanded={mobileOpen}
                aria-controls="header-mobile-menu"
                aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              >
                <i className="uc-icon" aria-hidden="true">
                  {mobileOpen ? 'close' : 'menu'}
                </i>
              </button>
            )}
            <span className="uc-h2 color-white headerMobile__uc">UC</span>
            <div className="headerMobile__actions">
              <LanguageToggle small onDark />
              {user && (
                <span
                  className="avatar avatar--circle avatar--s avatar--grey"
                  aria-label={`Sesión de ${user.name}`}
                >
                  <span className="avatar--initials">{initials}</span>
                </span>
              )}
            </div>
          </div>
          <div className="headerMobile__nameRow">
            <span className="text-font--serif color-white headerMobile__name">
              Plataforma de postulación Avanza UC
            </span>
          </div>
          {user && mobileOpen && (
            <div className="headerMobile__drawer" id="header-mobile-menu">
              {isAdmin && (
                <>
                  {location.pathname.startsWith('/researcher') && (
                    <Link to="/admin/dashboard" className="headerMobile__item">
                      Panel de Administración
                    </Link>
                  )}
                  {location.pathname.startsWith('/admin') && (
                    <Link to="/researcher/dashboard" className="headerMobile__item">
                      Panel Investigador
                    </Link>
                  )}
                  {canAccessUserManagement(user) && (
                    <Link to="/admin/users" className="headerMobile__item">
                      Usuarios
                    </Link>
                  )}
                </>
              )}
              <button type="button" className="headerMobile__item" onClick={handleLogout}>
                <i className="uc-icon" aria-hidden="true">
                  logout
                </i>
                Cerrar Sesión
              </button>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default AppHeader;
